import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Heading, Box, Text, Button, SimpleGrid, Image, Tag } from '@chakra-ui/react';
import { supabase } from '../supabaseClient';
import { RaceStats } from '../types/types';
import { positionConfig, getDisciplineColor } from '../config/styleConfig';
import BackButton from './shared/BackButton';

interface RaceData {
  race_code: string;
  race_description: string;
  race_date: string;
  race_location: string;
  race_discipline: string;
}

interface RacerStatsRecord {
  id: string;
  race_code: string;
  race_description: string;
  race_discipline: string;
  race_location: string;
  race_date: string;
  race_category: string;
  race_position: number;
  start_number: number;
  total_time: string | null;
  difference: string;
  country: string | null;
  first_run: string | null;
  second_run: string | null;
  name: string;
  year: number;
  team: string | null;
  category: string;
  lead: number;
  page: number;
  late: number;
  race_group: string;
  points: number;
  blc_points: string;
  reg_num_zsl: string;
  match_name_cluster_id: number;
  match_name_normalized: string;
}

interface RaceWithDetails extends Omit<RacerStatsRecord, 'gold_name' | 'gold_team' | 'gold_year' | 'gold_country'> {
  race_data?: {
    race_code: string;
    race_description: string;
    race_date: string;
    race_location: string;
    race_discipline: string;
  };
}

interface RacerBio {
  name: string;
  team: string | null;
  lastRace?: RaceWithDetails;
}

interface RacerInfo {
  name: string;
  category: string;
  year: number | null;
  team: string | null;
  country: string | null;
}

const getProfileImage = (name: string, category: string) => {
  // Convert name and category to lowercase for case-insensitive comparison
  const lowerName = name.toLowerCase();
  const lowerCategory = category.toLowerCase();

  // Check if it's a female racer
  const isFemale = 
    lowerName.endsWith('ová') || 
    lowerName.endsWith('ova') || 
    lowerCategory.includes('žiačky') || 
    lowerCategory.includes('predžiačky') || 
    lowerCategory.includes('dievčatá');

  return isFemale 
    ? '/img/skier-profile-default-girl2.jpeg'
    : '/img/skier-profile-default-boy2.jpeg';
};

// Add a sort function for races
const sortRacesByDate = (races: RaceWithDetails[]): RaceWithDetails[] => {
  return [...races].sort((a, b) => {
    const dateA = new Date(a.race_date);
    const dateB = new Date(b.race_date);
    return dateB.getTime() - dateA.getTime(); // newest first
  });
};

// Add helper function to convert 3-letter to 2-letter code
const convertToISO2 = (iso3: string): string => {
  const countryCodes: { [key: string]: string } = {
    'SVK': 'SK',
    'CZE': 'CZ',
    'POL': 'PL',
    'HUN': 'HU',
    'AUT': 'AT',
    // Add more mappings as needed
  };
  return countryCodes[iso3] || iso3.slice(0, 2);
};

const getCountryFlag = (countryCode: string): string => {
  // Take the first part (e.g., "SVK" from "SVK sk")
  const code = countryCode.split(' ')[0];
  // Convert to ISO2
  const iso2 = convertToISO2(code);
  return iso2.toLowerCase();
};

export default function RacerDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [races, setRaces] = useState<RaceWithDetails[]>([]);
  const [racerInfo, setRacerInfo] = useState<RacerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get the navigation state if it exists
  const locationState = location.state as { from?: string; teamName?: string } | null;

  useEffect(() => {
    const fetchRacerData = async () => {
      try {
        if (!name) {
          console.error('Missing racer name for RacerDetail query');
          setIsLoading(false);
          return;
        }

        // Call the database function to get all racer data
        const { data: racerDataRaw, error: racerError } = await (supabase as any)
          .rpc('get_frontend_race_stats_by_name', {
            p_name: name
          });

        if (racerError) {
          console.error('Error fetching racer data:', racerError);
          return;
        }

        const racerData = (racerDataRaw ?? []) as RacerStatsRecord[];

        if (racerData && racerData.length > 0) {
          // Sort races by date to get the latest race
          const sortedRaces = [...racerData].sort((a, b) => {
            const dateA = new Date(a.race_date);
            const dateB = new Date(b.race_date);
            return dateB.getTime() - dateA.getTime();
          });

          const latestRace = sortedRaces[0];

          // Year born: max(year) over all records where year <> 0 (same logic as SQL max(year) over (partition by null))
          const yearsNonZero = racerData
            .map((r) => (r.year != null ? parseInt(r.year.toString(), 10) : 0))
            .filter((y) => y !== 0);
          const yearBorn = yearsNonZero.length > 0 ? Math.max(...yearsNonZero) : null;

          // Set race history
          setRaces(racerData.map((record: RacerStatsRecord) => ({
            ...record,
            race_data: {
              race_code: record.race_code,
              race_description: record.race_description,
              race_date: record.race_date,
              race_location: record.race_location,
              race_discipline: record.race_discipline
            }
          })));

          // Set racer info using the latest race's category and computed year born
          setRacerInfo({
            name: racerData[0].name,
            team: racerData[0].team,
            year: yearBorn,
            country: racerData[0].country,
            category: latestRace.race_category || '' // Use category from latest race
          });
        }

        setIsLoading(false);
      } catch (e) {
        console.error('Exception in fetchRacerData:', e);
        setIsLoading(false);
      }
    };

    fetchRacerData();
  }, [name]);

  // Get the category from the first race (assuming racer's category is consistent)
  const category = racerInfo?.category || '';
  const profileImage = getProfileImage(name || '', category);

  // All teams from RPC data, ordered by most races first (for "Races for" section)
  const teamsOrdered = useMemo(() => {
    const count: Record<string, number> = {};
    races.forEach((r) => {
      const t = r.team || '';
      if (t) count[t] = (count[t] || 0) + 1;
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1]).map(([team]) => team);
  }, [races]);

  return (
    <Container maxW="container.xl" py={8}>
      <BackButton 
        to={locationState?.from === 'teamDetail' && locationState.teamName 
          ? `/teamDetail/${encodeURIComponent(locationState.teamName)}` 
          : "/racers"} 
        label={locationState?.from === 'teamDetail' && locationState.teamName
          ? `Back to ${locationState.teamName}`
          : "Back to Racers"} 
      />
      
      <Box px={4} pt={2}>
        <Heading mb={8}>Racer Profile: {name}</Heading>
        
        {/* Profile Section */}
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" mb={8}>
          <SimpleGrid columns={[1, 2]} gap={6}>
            <Box>
              <Image
                src={profileImage}
                alt={`${name}'s profile`}
                borderRadius="md"
                maxH="300px"
                objectFit="cover"
                objectPosition="top"
                fallback={<Box bg="gray.200" h="300px" borderRadius="md" />}
              />
            </Box>
            <Box>
              <Heading size="lg" mb={2}>{name}</Heading>
              {racerInfo?.country && (
                <Text mb={2} color="gray.600" display="flex" alignItems="center" gap={2}>
                  Nationality: {racerInfo.country.split(' ')[0]} 
                  <Box 
                    as="span" 
                    display="inline-block"
                    width="24px"
                    height="16px"
                    backgroundImage={`url(https://flagcdn.com/${getCountryFlag(racerInfo.country)}.svg)`}
                    backgroundSize="cover"
                    backgroundPosition="center"
                    borderRadius="2px"
                  />
                </Text>
              )}
              <Text mb={2} color="gray.600">
                Year Born: {racerInfo?.year != null && racerInfo.year !== 0
                  ? racerInfo.year
                  : '—'}
              </Text>
              {teamsOrdered.length > 0 && (
                <Box mb={4}>
                  <Text color="gray.600" mb={1}>Races for:</Text>
                  {teamsOrdered.map((team) => (
                    <Text
                      key={team}
                      display="block"
                      color="gray.600"
                      cursor="pointer"
                      _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                      onClick={() => navigate(`/teamDetail/${encodeURIComponent(team)}`)}
                    >
                      {team}
                    </Text>
                  ))}
                </Box>
              )}
              <Text mb={2}><strong>Category:</strong> {category}</Text>
              <Text mb={2}><strong>Total Races:</strong> {races.length}</Text>
              <Text fontSize="xl" mb={3}>
                <strong>Best Position:</strong> {Math.min(...races.map(r => Number(r.race_position) || Infinity))}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        <Heading size="md" mb={8}>Race History for {name}</Heading>
        
        {sortRacesByDate(races).map(race => (
          <Box key={race.id} p={4} mb={4} borderWidth="1px" borderRadius="md">
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Text 
                color="inherit"
                cursor="pointer"
                _hover={{ color: 'darkblue', textDecoration: 'underline' }}
                onClick={() => navigate(`/race/${encodeURIComponent(race.race_code)}`)}
              >
                <strong>Race:</strong> {race.race_data?.race_description || race.race_code}
              </Text>
              {race.race_data?.race_discipline && (
                <Tag
                  size="sm"
                  bg={getDisciplineColor(race.race_data.race_discipline)}
                  color="white"
                  variant="solid"
                >
                  {race.race_data.race_discipline}
                </Tag>
              )}
            </Box>
            <Text><strong>Date:</strong> {new Date(race.race_date).toLocaleDateString()}</Text>
            <Text 
              color="inherit"
              cursor="pointer"
              _hover={{ color: 'darkblue', textDecoration: 'underline' }}
              onClick={() => navigate(`/category/${encodeURIComponent(race.race_category)}?race_code=${race.race_code}`)}
            >
              <strong>Category:</strong> {race.race_category}
            </Text>
            <Box display="flex" alignItems="center" gap={2}>
              <Text>
                <strong>Position:</strong> {race.race_position === 0 ? 'DNF' : race.race_position}
              </Text>
              {race.race_position !== 0 && positionConfig[race.race_position.toString() as keyof typeof positionConfig] && (
                <Tag
                  size="sm"
                  variant="subtle"
                  bg={positionConfig[race.race_position.toString() as keyof typeof positionConfig].color}
                  color="white"
                >
                  {positionConfig[race.race_position.toString() as keyof typeof positionConfig].label}
                </Tag>
              )}
            </Box>
            <Text><strong>Time:</strong> {race.total_time}</Text>
            <Text><strong>First Run:</strong> {race.first_run}</Text>
            <Text><strong>Second Run:</strong> {race.second_run}</Text>
            <Text><strong>Location:</strong> {race.race_data?.race_location}</Text>
          </Box>
        ))}
      </Box>
    </Container>
  );
}